import React, { useMemo, useSyncExternalStore } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WidgetsProvider } from '@logspot-platform/widgets';
import type { FilterGroup } from '@logspot-platform/widgets';
import './styles/globals.css';
import 'react-tooltip/dist/react-tooltip.css';
import { Dashboard } from './app';
import { createOttApi } from './lib/api';
import {
  getEmbedConfig,
  initEmbedConfig,
  subscribeEmbedConfig,
} from './lib/config-store';
import { decodeProjectId } from './lib/decode-token';
import { DEFAULT_WIDGETS } from './lib/default-widgets';
import { initExecuteBridge } from './lib/execute';
import { startResizeReporting } from './lib/resize';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10000,
      retry: 1,
    },
  },
});

// The token's filters are enforced server-side; the embed sends no
// client-side filters of its own.
const NO_FILTERS: FilterGroup[] = [];

const App = () => {
  const config = useSyncExternalStore(subscribeEmbedConfig, getEmbedConfig);
  const accessToken = config?.accessToken;

  const api = useMemo(
    () => (accessToken ? createOttApi(accessToken) : null),
    [accessToken],
  );
  const projectId = useMemo(
    () => (accessToken ? (decodeProjectId(accessToken) ?? '') : ''),
    [accessToken],
  );

  if (!accessToken || !api) {
    return null;
  }

  return (
    <WidgetsProvider
      api={api}
      filterGroups={NO_FILTERS}
      projectId={projectId}
    >
      <Dashboard widgets={config.widgets ?? DEFAULT_WIDGETS} />
    </WidgetsProvider>
  );
};

initEmbedConfig();
initExecuteBridge();
startResizeReporting();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Could not find root element');
}
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
